import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Bug,
  ChevronDown,
  ChevronUp,
  Flower2,
  Leaf,
  Ruler,
  ShieldCheck,
  Sun,
  X,
} from 'lucide-react';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import ToxicWarningBadge from '../Editor/PropertiesPanels/ToxicWarningBadge';
import PlantDetailDialog from '../Editor/PropertiesPanels/PlantDetailDialog';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { getPlantImageUrl } from '../../utils/plantImageUtils';
import {
  SUNLIGHT_LABELS,
  WATER_REQUIREMENT_LABELS,
  SOIL_TYPE_LABELS,
  PLANT_TYPE_LABELS,
  PRUNING_TYPE_LABELS,
  translateEnum,
  translateEnumList,
} from '../../utils/plantEnumLabels';

/**
 * PlantDetailSheet — plant detail view used from PlantListView.
 *
 * Desktop: delegates to the existing PlantDetailDialog (full 11-section modal).
 * Mobile: bottom sheet with 5 default-open + 2 collapsed accordion sections.
 *
 * Sections (mobile):
 *  0 – Hero (image + name + tox badge)       open
 *  1 – Standort & Boden                       open
 *  2 – Pflege                                 open
 *  3 – Wuchs                                  open
 *  4 – Eigenschaften                          open
 *  5 – Verwendung                             collapsed
 *  6 – Botanisch                              collapsed
 */

// ─── Helpers ───────────────────────────────────────────────────────────────

// Labels für sunlight / water / soil / plant_type / pruning_type kommen zentral
// aus plantEnumLabels (#751). Pflegeaufwand behält hier bewusst eigene
// Kurz-Labels + Farbklassen und Wuchstempo eine eigene Kurzform.
const CARE_PRIORITY_MAP = {
  low: { label: 'Niedrig', cls: 'bg-green-100 text-green-800' },
  medium: { label: 'Mittel', cls: 'bg-yellow-100 text-yellow-800' },
  high: { label: 'Hoch', cls: 'bg-red-100 text-red-800' },
};

const GROWTH_SPEED_MAP = {
  slow: 'Langsam', medium: 'Mittel', fast: 'Schnell',
};

const displaySunlight = (v) =>
  translateEnumList(SUNLIGHT_LABELS, v, 'sunlight') || 'Unbekannt';

const displaySoil = (types) =>
  translateEnumList(SOIL_TYPE_LABELS, types, 'soil_type', ', ');

const displayHeight = (plant) => {
  const min = plant.min_height_cm ?? plant.properties?.min_height_cm;
  const max = plant.max_height_cm ?? plant.properties?.max_height_cm;
  if (min && max) return `${min} – ${max} cm`;
  if (min) return `ab ${min} cm`;
  if (max) return `bis ${max} cm`;
  return null;
};

const displayWidth = (plant) => {
  const min = plant.min_width_cm ?? plant.properties?.min_width_cm;
  const max = plant.max_width_cm ?? plant.properties?.max_width_cm;
  if (min && max) return `${min} – ${max} cm`;
  if (min) return `ab ${min} cm`;
  if (max) return `bis ${max} cm`;
  return null;
};

// ─── AccordionSection ──────────────────────────────────────────────────────

const AccordionSection = ({ icon: Icon, title, isOpen, onToggle, children }) => (
  <div className="border-b border-gray-100">
    <button
      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-500" />}
        <span className="text-sm font-semibold text-gray-900">{title}</span>
      </div>
      {isOpen
        ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
        : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      }
    </button>
    {isOpen && (
      <div className="px-4 pb-4 space-y-2">
        {children}
      </div>
    )}
  </div>
);

// ─── InfoRow ───────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-xs text-gray-500 min-w-[96px] shrink-0">{label}</span>
      <span className="text-xs text-gray-900">{value}</span>
    </div>
  );
};

// ─── MobilePlantDetailSheet ────────────────────────────────────────────────

const SECTION_COUNT = 7;
const DEFAULT_OPEN = new Set([0, 1, 2, 3, 4]);

const MobilePlantDetailSheet = ({ plant, isOpen, onClose }) => {
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN);
  const [prevPlantId, setPrevPlantId] = useState(plant?.id);
  const { settings } = useGardenSettings();

  // Reset accordion when plant changes — adjusted during render rather than in an
  // effect to avoid a setState-in-effect cascade.
  if (plant?.id !== prevPlantId) {
    setPrevPlantId(plant?.id);
    if (plant) setOpenSections(DEFAULT_OPEN);
  }

  // Block body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const toggleSection = useCallback((idx) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  if (!plant) return null;

  const displayName = plant.german_name || plant.name;
  const imageName = plant.name || plant.german_name;
  const props = plant.properties || {};
  const care = plant.care_instructions || {};

  const sunlight = plant.sunlight || props.sunlight;
  const water = plant.water_requirement || props.water_requirement;
  const soilType = plant.soil_type || props.soil_type;
  const isToxic = plant.toxic_to_pets || plant.toxic_to_children ||
    (plant.toxicity_level && plant.toxicity_level !== 'none');
  const toxicityLevel = props.toxicity_level || plant.toxicity_level || 'none';
  const growthSpeed = plant.growth_speed || props.growth_speed;
  const evergreen = plant.evergreen ?? props.evergreen;
  const frostHardy = plant.frost_hardy ?? props.frost_hardy;
  const suitableForPot = plant.suitable_for_pot ?? props.suitable_for_pot;
  const beeFriendly = plant.bee_friendly ?? props.bee_friendly;
  const hardinessZone = plant.hardiness_zone || props.hardiness_zone;
  const companionPlants = (props.companion_plants || []).join(', ') || null;
  const carePriority = care.priority;

  const beeFriendlyLabel = typeof beeFriendly === 'string'
    ? ({ none: 'Keine', low: 'Gering', medium: 'Mittel', high: 'Hoch' }[beeFriendly] || null)
    : beeFriendly === true ? 'Ja' : beeFriendly === false ? 'Nein' : null;

  const sheet = (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
        data-testid="plant-detail-backdrop"
      />

      {/* Sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl flex flex-col transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: '90dvh' }}
        role="dialog"
        aria-modal="true"
        aria-label={displayName}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-9 h-1 bg-gray-300 rounded-full" data-testid="drag-handle" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Schließen"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-6">

          {/* Section 0 — Hero */}
          <AccordionSection
            icon={Flower2}
            title={displayName || 'Pflanze'}
            isOpen={openSections.has(0)}
            onToggle={() => toggleSection(0)}
          >
            {/* Image */}
            {imageName && (
              <div className="flex justify-center mb-3">
                <img
                  src={getPlantImageUrl(imageName, 'catalog')}
                  alt={displayName}
                  className="w-32 h-32 rounded-xl object-cover bg-emerald-50"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            {plant.scientific_name && (
              <p className="text-xs italic text-gray-500 text-center mb-2">
                {plant.scientific_name}
              </p>
            )}
            {/* Tox badge */}
            <ToxicWarningBadge
              isToxic={isToxic}
              toxicityLevel={toxicityLevel}
              toxicToPets={plant.toxic_to_pets}
              toxicToChildren={plant.toxic_to_children}
              toxicToCats={plant.toxic_to_cats}
              toxicToDogs={plant.toxic_to_dogs}
              country={settings.country}
            />
          </AccordionSection>

          {/* Section 1 — Standort & Boden */}
          <AccordionSection
            icon={Sun}
            title="Standort & Boden"
            isOpen={openSections.has(1)}
            onToggle={() => toggleSection(1)}
          >
            <InfoRow label="Sonnenlicht" value={sunlight ? displaySunlight(sunlight) : null} />
            <InfoRow label="Wasserbedarf" value={translateEnum(WATER_REQUIREMENT_LABELS, water, 'water_requirement')} />
            <InfoRow label="Boden" value={displaySoil(soilType)} />
          </AccordionSection>

          {/* Section 2 — Pflege */}
          <AccordionSection
            icon={Leaf}
            title="Pflege"
            isOpen={openSections.has(2)}
            onToggle={() => toggleSection(2)}
          >
            {carePriority && CARE_PRIORITY_MAP[carePriority] && (
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-500 min-w-[96px] shrink-0">Pflegeaufwand</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CARE_PRIORITY_MAP[carePriority].cls}`}>
                  {CARE_PRIORITY_MAP[carePriority].label}
                </span>
              </div>
            )}
            <InfoRow label="Bewässerung" value={translateEnum(WATER_REQUIREMENT_LABELS, water, 'water_requirement')} />
            {care.pruning_type && care.pruning_type !== 'none' && (
              <InfoRow label="Schnitt" value={translateEnum(PRUNING_TYPE_LABELS, care.pruning_type, 'pruning_type')} />
            )}
            {plant.drought_tolerant !== undefined && (
              <InfoRow label="Trockenheitstolerant" value={plant.drought_tolerant ? 'Ja' : 'Nein'} />
            )}
          </AccordionSection>

          {/* Section 3 — Wuchs */}
          <AccordionSection
            icon={Ruler}
            title="Wuchs"
            isOpen={openSections.has(3)}
            onToggle={() => toggleSection(3)}
          >
            <InfoRow label="Wuchshöhe" value={displayHeight(plant)} />
            <InfoRow label="Wuchsbreite" value={displayWidth(plant)} />
            <InfoRow label="Wuchsgeschwindigkeit" value={growthSpeed ? (GROWTH_SPEED_MAP[growthSpeed] || growthSpeed) : null} />
          </AccordionSection>

          {/* Section 4 — Eigenschaften */}
          <AccordionSection
            icon={isToxic ? AlertTriangle : ShieldCheck}
            title="Eigenschaften"
            isOpen={openSections.has(4)}
            onToggle={() => toggleSection(4)}
          >
            {evergreen !== undefined && <InfoRow label="Immergrün" value={evergreen ? 'Ja' : 'Nein'} />}
            {frostHardy !== undefined && <InfoRow label="Frosthart" value={frostHardy ? 'Ja' : 'Nein'} />}
            {suitableForPot !== undefined && <InfoRow label="Kübel geeignet" value={suitableForPot ? 'Ja' : 'Nein'} />}
            {beeFriendlyLabel && <InfoRow label="Bienenfreundlich" value={beeFriendlyLabel} />}
          </AccordionSection>

          {/* Section 5 — Verwendung (collapsed) */}
          <AccordionSection
            icon={Bug}
            title="Verwendung"
            isOpen={openSections.has(5)}
            onToggle={() => toggleSection(5)}
          >
            <InfoRow label="Gute Nachbarn" value={companionPlants || 'Keine'} />
          </AccordionSection>

          {/* Section 6 — Botanisch (collapsed) */}
          <AccordionSection
            icon={Flower2}
            title="Botanisch"
            isOpen={openSections.has(SECTION_COUNT - 1)}
            onToggle={() => toggleSection(SECTION_COUNT - 1)}
          >
            <InfoRow label="Familie" value={plant.family || props.family || null} />
            <InfoRow label="Typ" value={translateEnum(PLANT_TYPE_LABELS, plant.plant_type || props.plant_type, 'plant_type')} />
            <InfoRow label="Winterhärte" value={hardinessZone || null} />
            <InfoRow label="Heimatregion" value={plant.native_region || props.native_region || null} />
          </AccordionSection>

        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
};

// ─── PlantDetailSheet ──────────────────────────────────────────────────────

/**
 * PlantDetailSheet — unified plant detail entry point.
 *
 * Desktop (≥ 768px): opens the full PlantDetailDialog.
 * Mobile (< 768px): opens MobilePlantDetailSheet (bottom sheet + accordion).
 */
const PlantDetailSheet = ({ isOpen, onClose, plant }) => {
  const { isMobile } = useBreakpoint();

  if (!isMobile) {
    return (
      <PlantDetailDialog
        isOpen={isOpen}
        onClose={onClose}
        plant={plant}
      />
    );
  }

  return (
    <MobilePlantDetailSheet
      isOpen={isOpen}
      onClose={onClose}
      plant={plant}
    />
  );
};

export default PlantDetailSheet;
